// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';

import type { PlanWallSet, XY } from '../../bygning/planWalls';
import type { ViewportHandle } from '../../viewport/Viewport';
import styles from './PlanWalls.module.css';

const NONE: readonly string[] = [];

/** An opening narrower than this on screen (px) carries no label. */
const MIN_LABEL_PX = 40;

export interface PlanWallsProps {
  viewport: RefObject<ViewportHandle | null>;
  set: PlanWallSet;
  /** Bump to reproject (content landed). */
  revision: number;
  /** The walls' and openings' guids drawn in the selection colour (Rediger › Geometri). */
  selected?: readonly string[];
  /** Rediger › Geometri's CAD plan: the editor's wall fill, no opening labels. */
  cad?: boolean;
}

/**
 * The building model's walls and openings on the locked plan
 * (frontend-4rt, drawing conventions frontend-r22.27): the walls the plan
 * cuts as solid poché, walls seen beyond the cut (a parapet, the storeys
 * below) as thin lines, each opening cut out of its wall with a door's
 * leaf and swing arc or a window's three lines, and a label
 * ("Dør · 0,90 m") where there is room for one. Under the spaces'
 * names and the panorama markers (`PlanLayer`); takes no pointer.
 */
export function PlanWalls({ viewport, set, revision, selected = NONE, cad = false }: PlanWallsProps) {
  const picked = useMemo(() => new Set(selected), [selected]);
  const [tick, setTick] = useState(0);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let frame: number | null = null;
    const bump = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        setTick((t) => t + 1);
      });
    };
    const off = viewport.current?.onCameraChange(bump) ?? (() => {});
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(bump);
    if (hostRef.current) observer?.observe(hostRef.current);
    bump();
    return () => {
      off();
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [viewport, revision]);

  const handle = viewport.current;
  const drawn = useMemo(() => {
    if (!handle) return null;
    const path = (pts: readonly XY[], z: number, close: boolean): string | null => {
      const xyz = new Float64Array(pts.length * 3);
      pts.forEach((p, i) => xyz.set([p[0], p[1], z], i * 3));
      const s = handle.projectWorld(xyz, { clip: false });
      let d = '';
      for (let i = 0; i < pts.length; i += 1) {
        const x = s[i * 2];
        const y = s[i * 2 + 1];
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
        d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
      }
      return close ? `${d}Z` : d;
    };
    const walls = set.walls.map((w) => ({
      w,
      outline: path(w.outline, w.z, w.outline.length > 2),
      // A wall with no thickness is its axis alone; poché needs no axis drawn through it.
      axis: w.outline.length > 2 && !w.cut ? path(w.axis, w.z, false) : null,
    }));
    const openings = set.openings.map((o) => {
      const c = handle.projectWorld(
        new Float64Array([o.labelAt[0], o.labelAt[1], o.z, o.labelAt[0] + 1, o.labelAt[1], o.z]),
        { clip: false },
      );
      const ppm = Math.hypot(c[2] - c[0], c[3] - c[1]);
      return {
        o,
        cut: path(o.cut, o.z, o.cut.length > 2),
        leaves: o.leaves.map((l) => path(l, o.z, false)),
        swings: o.swings.map((a) => path(a, o.z, false)),
        panes: o.panes.map((p) => path(p, o.z, false)),
        at: Number.isFinite(c[0]) && Number.isFinite(c[1]) ? ([c[0], c[1]] as const) : null,
        widthPx: o.width * ppm,
      };
    });
    return { walls, openings };
    // `tick` is the camera; the handle's projection reads it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle, set, tick]);

  return (
    <div ref={hostRef} className={`${styles.layer}${set.ghost ? ` ${styles.ghost}` : ''}${cad ? ` ${styles.cad}` : ''}`} aria-hidden="true">
      <svg className={styles.svg}>
        {/* Seen beyond the cut first, then the cut walls' poché over them. */}
        {drawn?.walls
          .filter(({ w }) => !w.cut)
          .map(({ w, outline }) =>
            outline ? (
              <path key={w.guid} data-guid={w.guid} d={outline} className={`${styles.wall} ${styles.beyond}${picked.has(w.guid) ? ` ${styles.selected}` : ''}`} />
            ) : null,
          )}
        {drawn?.walls
          .filter(({ w }) => w.cut)
          .map(({ w, outline, axis }) => (
            // data-guid: the E2E specs' hook for "is this wall drawn / selected".
            <g key={w.guid} data-guid={w.guid}>
              {outline && (
                <path
                  d={outline}
                  className={`${styles.wall} ${styles.poche}${w.assumed ? ` ${styles.assumed}` : ''}${picked.has(w.guid) ? ` ${styles.selected}` : ''}`}
                />
              )}
              {axis && <path d={axis} className={styles.axis} />}
            </g>
          ))}
        {drawn?.openings.map(({ o, cut, leaves, swings, panes }) => (
          <g key={o.guid} data-guid={o.guid} className={picked.has(o.guid) ? `${styles.opening} ${styles.selectedOpening}` : styles.opening}>
            {cut && <path d={cut} className={styles.cut} />}
            {leaves.map((d, i) => (d ? <path key={`l${i}`} d={d} className={styles.leaf} /> : null))}
            {swings.map((d, i) => (d ? <path key={`s${i}`} d={d} className={styles.swing} /> : null))}
            {panes.map((d, i) => (d ? <path key={i} d={d} className={styles.pane} /> : null))}
          </g>
        ))}
      </svg>
      {drawn?.openings.map(({ o, at, widthPx }) =>
        at && widthPx >= MIN_LABEL_PX ? (
          <span key={o.guid} className={styles.label} style={{ transform: `translate(${at[0].toFixed(1)}px, ${at[1].toFixed(1)}px) translate(-50%, -50%)` }}>
            {o.label}
          </span>
        ) : null,
      )}
    </div>
  );
}
